import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { ArrowLeft } from "lucide-react";
import { collection, addDoc, serverTimestamp, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

const API = process.env.REACT_APP_BACKEND_URL;

const JobDetail = () => {
  const { slug }  = useParams();
  if (!slug) {
  return <div className="min-h-screen flex items-center justify-center">Invalid job link</div>;
}

  const navigate = useNavigate();

  const nameInputRef = useRef(null);

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    resume: null,
  });

  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [firebaseError, setFirebaseError] = useState("");

  // ===============================
  // 🔹 FETCH JOB FROM FIREBASE
  // ===============================
  useEffect(() => {
    const fetchJob = async () => {
      try {
        const ref = doc(db, "jobs", slug);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          setJob({ id: snap.id, ...snap.data() });
        } else {
          setJob(null);
        }
      } catch (err) {
        console.error("Error fetching job:", err);
        setJob(null);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
    fetchJob();
  }
  }, [slug]);

  // ===============================
  // 🔹 VALIDATION
  // ===============================
  useEffect(() => {
    const errs = {};

    if (touched.name) {
      if (!form.name) errs.name = "Full name is required";
      else if (!/^[A-Z][a-z]*(\s[A-Z][a-z]*)*$/.test(form.name))
        errs.name = "Each word should start with a capital letter";
    }

    if (touched.email) {
      if (!form.email) errs.email = "Email is required";
      else if (!/^[a-zA-Z0-9._%+-]+@gmail\.com$/.test(form.email))
        errs.email = "Email must be a valid email include @gmail.com";
    }

    if (touched.phone) {
      if (!form.phone) errs.phone = "Phone number is required";
      else if (form.phone.length !== 10)
        errs.phone = "Phone number must be exactly 10 digits";
    }

    if (touched.resume && form.resume) {
      const validTypes = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!validTypes.includes(form.resume.type)) errs.resume = "Invalid file type";
    }

    setErrors(errs);
  }, [form, touched]);

  // ===============================
  // 🔹 HANDLERS (NO HOOKS HERE)
  // ===============================
  const handleNameChange = (e) => {
    const input = e.target;
    let value = input.value.replace(/[^a-zA-Z ]/g, "");

    const start = input.selectionStart;
    const end = input.selectionEnd;

    const capitalized = value.replace(/\b[a-z]/g, (char) => char.toUpperCase());

    setForm((prev) => ({ ...prev, name: capitalized }));

    requestAnimationFrame(() => {
      input.setSelectionRange(start, end);
    });

    setTouched((prev) => ({ ...prev, name: true }));
  };

  const handlePhoneChange = (e) => {
    let digits = e.target.value.replace(/\D/g, "");
    if (digits.length > 10) return;

    setForm((prev) => ({ ...prev, phone: digits }));
    setTouched((prev) => ({ ...prev, phone: true }));
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "resume") {
      setForm((prev) => ({ ...prev, resume: files[0] }));
      setTouched((prev) => ({ ...prev, resume: true }));
    } else if (name !== "name") {
      setForm((prev) => ({ ...prev, [name]: value }));
      setTouched((prev) => ({ ...prev, [name]: true }));
    }
  };

  const roleFromCareers = job?.title || "";

 
    const isFormValid =
  form.name &&
  form.email &&
  form.phone &&
  form.resume &&
  Object.keys(errors).length === 0;


  // ===============================
  // 🔹 SUBMIT HANDLER
  // ===============================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, email: true, phone: true, resume: true });

    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);
    setFirebaseError("");

    try {
      const formData = new FormData();
      formData.append("jobTitle", roleFromCareers);
      formData.append("name", form.name);
      formData.append("email", form.email);
      formData.append("phone", form.phone);
      formData.append("message", form.message || "");

      if (form.resume) {
        formData.append("resume", form.resume);
      }

      const uploadRes = await fetch(`${API}/upload-resume`, {
        method: "POST",
        body: formData,
      });

      const uploadData = await uploadRes.json();

      if (!uploadData.resumeURL) {
        throw new Error("Resume upload failed");
      }

      const resumeName = uploadData.resumeName;
      const resumeURL = uploadData.resumeURL;

      await addDoc(collection(db, "jobApplications"), {
        jobId: slug,
        jobTitle: roleFromCareers,
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: form.message || "",
        resumeName: resumeName,
        resumeURL: resumeURL,
        status: "New",
        createdAt: serverTimestamp(),
      });
      // 🔥 SEND EMAIL (VERY IMPORTANT)
await fetch(`${API}/send-job-application-email`, {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    name: form.name,
    email: form.email,
    phone: form.phone,
    jobTitle: roleFromCareers,
    resumeName: resumeName,
    resumeURL: resumeURL,
    message: form.message || "",
  }),
});


      setSubmitStatus("success");

      setForm({
        name: "",
        email: "",
        phone: "",
        message: "",
        resume: null,
      });

      setTouched({});
    } catch (error) {
      console.error("Application submit error:", error);
      setFirebaseError("Something went wrong. Please try again.");
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ===============================
  // 🔹 RETURNS (ONLY HERE)
  // ===============================
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-gray-500">Loading job...</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-gray-500">Job not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-2)] pt-28 pb-16">
      <div className="container-custom mb-12 relative text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-brand-dark mb-3 mt-2">
          {job.title}
        </h1>

        <p className="text-gray-600 mt-2">
          {job.location} • {job.type}
        </p>

        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-brand-dark hover:underline mt-2"
        >
          <ArrowLeft size={18} /> Back
        </button>
      </div>

      <div className="container-custom grid lg:grid-cols-3 gap-10">
        {/* LEFT */}
        <div className="lg:col-span-2 bg-white p-8 rounded-md border shadow-sm">
          {/* JOB SUMMARY */}
<h3 className="font-semibold text-brand-dark mb-2">Job Summary</h3>
<p className="text-gray-700 mb-6 leading-relaxed">
  {job.summary}
</p>

{/* RESPONSIBILITIES */}
<h3 className="font-semibold text-brand-dark mb-2">Responsibilities</h3>
<ul className="list-disc pl-5 text-gray-700 space-y-2 mb-6">
  {job.responsibilities?.map((item, index) => (
    <li key={index}>{item}</li>
  ))}
</ul>

{/* REQUIREMENTS */}
<h3 className="font-semibold text-brand-dark mb-2">Requirements</h3>
<ul className="list-disc pl-5 text-gray-700 space-y-2 mb-6">
  {job.requirements?.map((item, index) => (
    <li key={index}>{item}</li>
  ))}
</ul>

{/* EXPERIENCE */}
<h3 className="font-semibold text-brand-dark mb-2">Experience</h3>
<p className="text-gray-700">
  {job.experience}
</p>

        </div>

        {/* RIGHT */}
          <div className="bg-white p-6 rounded-md border shadow-sm sticky top-28">
            <h3 className="text-xl font-bold text-brand-dark mb-4">Apply for this job</h3>

            {submitStatus === "success" ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-green-600 text-center text-lg font-semibold">
                  Application submitted successfully!
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Full Name <span className="text-red-500">*</span></Label>
                  <Input
                    ref={nameInputRef}
                    name="name"
                    value={form.name}
                    onChange={handleNameChange}
                    placeholder="Your Name"
                  />
                  {errors.name && (
                    <p className="text-red-600 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <Label>Email <span className="text-red-500">*</span></Label>
                  <Input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="example@mail.com"
                  />
                  {errors.email && (
                    <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                  )}
                </div>

                <div>
  <Label>Phone <span className="text-red-500">*</span></Label>

  <div className="relative">
    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
      +91
    </span>

    <Input
      name="phone"
      value={form.phone}
      onChange={handlePhoneChange}
      placeholder="XXXXXXXXXX"
      className="pl-12"
      inputMode="numeric"
      maxLength={10}
    />
  </div>

  {errors.phone && (
    <p className="text-red-600 text-sm mt-1">{errors.phone}</p>
  )}
</div>


                <div>
                  <Label>Applying For</Label>
                  <Input value={roleFromCareers} disabled />
                </div>

                <div>
                  <Label>Upload Resume <span className="text-red-500">*</span></Label>
                  <Input
                    type="file"
                    name="resume"
                    accept=".pdf,.doc,.docx"
                    onChange={handleChange}
                  />
                  {errors.resume && (
                    <p className="text-red-600 text-sm mt-1">{errors.resume}</p>
                  )}
                </div>

                <div>
                  <Label>Message (Optional)</Label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Additional information..."
                    className="w-full border rounded p-2"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full btn-primary mt-4"
                  disabled={!isFormValid || isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Submit Application"}
                </Button>

                {firebaseError && (
                  <p className="text-red-600 mt-2">{firebaseError}</p>
                )}
              </form>
            )}
          </div>
      </div>
    </div>
  );
};

export default JobDetail;
